import { useState } from 'react';
import { newSubmissionId, verifyText } from '../lib/api.js';
import { enqueue, isOnline } from '../lib/submissionQueue.js';

export default function TextSubmission({ tripId, playerId, challengeId, testMode, onVerified, minWords = 0 }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState(null);

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const tooShort = wordCount < minWords;

  async function queueOffline() {
    await enqueue({
      trip_id: tripId,
      player_id: playerId,
      challenge_id: challengeId,
      submission_type: 'text',
      text,
      timestamp_claimed: new Date().toISOString(),
      test_mode: !!testMode,
    });
    setQueued(true);
    setText('');
  }

  async function submit() {
    if (!text.trim() || tooShort) return;
    setError(null);
    setBusy(true);

    if (!isOnline()) {
      try {
        await queueOffline();
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setBusy(false);
      }
      return;
    }

    try {
      const result = await verifyText({
        tripId,
        challengeId,
        submissionId: newSubmissionId(),
        text,
        testMode,
      });
      onVerified?.(result);
      setText('');
    } catch (err) {
      if (!isOnline() || /failed to fetch|network|offline/i.test(err.message || '')) {
        try {
          await queueOffline();
          return;
        } catch (qerr) {
          setError(qerr.message || String(qerr));
        }
      } else {
        setError(err.message || String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder="Your submission…"
        className="w-full resize-y rounded-md border border-border-tertiary bg-bg-primary px-3 py-2 font-serif text-base text-fg-primary placeholder:text-fg-tertiary focus:border-action focus:outline-none"
      />
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        <span>{wordCount} word{wordCount === 1 ? '' : 's'}{minWords ? ` · need ${minWords}+` : ''}</span>
        <button
          type="button"
          onClick={submit}
          disabled={busy || tooShort || !text.trim()}
          className="rounded-md border border-action bg-action px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-action-fg transition hover:bg-action-hover disabled:opacity-60"
        >
          {busy ? 'verifying…' : 'submit'}
        </button>
      </div>
      {queued && (
        <p className="rounded-md border border-copper/40 bg-copper/10 px-3 py-2 font-mono text-[11px] text-copper-dark">
          Queued offline · drains automatically when signal returns.
        </p>
      )}
      {error && (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
